/**
 * First-gesture engine start + a "has started" flag React can subscribe to (the engine itself has no events).
 */
import type { AudioEngine } from '../engine/types';
import type { Drone } from '@tonist/shared';
import type { MandalaRenderer } from '../mandala/renderer-api';

/** Optional engine/renderer extras that other tasks may or may not implement. */
export type EngineWithExtras = AudioEngine & { updateDrone?(drone: Drone): void };
export type RendererWithExtras = MandalaRenderer & { setPrayerWheel?(omega: number): void };

let started = false;
let starting: Promise<void> | null = null;
const listeners = new Set<() => void>();

export const engineStarted = (): boolean => started;

export function onEngineStarted(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Call from a user gesture. Resolves once the engine is running; safe to call repeatedly. */
export function ensureEngineStarted(engine: AudioEngine): Promise<void> {
  if (started) return Promise.resolve();
  if (starting) return starting;
  starting = engine.start().then(() => {
    started = true;
    starting = null;
    for (const fn of listeners) fn();
  }).catch((e) => { starting = null; throw e; });
  return starting;
}

/** Only used by tests / hot reload. */
export function _resetEngineBoot(): void { started = false; starting = null; listeners.clear(); }
